import posthog from 'posthog-js';

import { isAnalyticsAllowed, isMarketingPath } from '@/lib/tracking/consent';
import { beforeSend } from '@/lib/tracking/utils/beforeSend';

const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
] as const;

function readCampaignParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const found: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = params.get(key);
    if (value) {
      found[key] = value;
    }
  }
  return found;
}

// Campaign params of the first full page load, kept in memory only so nothing is stored
// before consent. posthog-js reads them from the URL at its first capture; when the visitor
// soft-navigates before accepting, the URL no longer has them and these are registered instead.
const landingCampaignParams =
  typeof window === 'undefined' ? {} : readCampaignParams(window.location.search);

/**
 * The app's only `posthog.init`. Runs from instrumentation-client.ts on every full page load,
 * from the cookie banner on Accept and on every route change. On marketing pages it waits for
 * the visitor to accept cookies and never records sessions.
 */
export function initPostHogIfAllowed(pathname: string = window.location.pathname): void {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!posthogKey || process.env.NEXT_PUBLIC_DISABLE_POSTHOG || posthog.__loaded) {
    return;
  }
  if (!isAnalyticsAllowed(pathname)) {
    return;
  }

  posthog.init(posthogKey, {
    api_host: '/ingest',
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.posthog.com',
    defaults: '2025-05-24',
    capture_exceptions: true, // This enables capturing exceptions using Error Tracking
    before_send: beforeSend,
    debug: process.env.NODE_ENV === 'development',
    disable_session_recording: isMarketingPath(pathname),
    // Add tracing headers for server-side error context
    __add_tracing_headers: [process.env.NEXT_PUBLIC_SITE_URL || 'localhost:3000'],
  });

  // The initial $pageview is captured on the next tick, so it carries these.
  const hasCurrentParams = Object.keys(readCampaignParams(window.location.search)).length > 0;
  if (!hasCurrentParams && Object.keys(landingCampaignParams).length > 0) {
    posthog.register_for_session(landingCampaignParams);
  }
}
